import { z } from "zod";

import {
  FRENCH_PHONE_INVALID_MESSAGE,
  isOptionalFrenchPhoneValid,
} from "@/lib/cat/french-phone";

export const SEARCH_NAME_REGEX =
  /^[A-Za-zÀ-ÖØ-öø-ÿ]{2,}(?:[ '-][A-Za-zÀ-ÖØ-öø-ÿ]{2,})*$/;

export const POSTAL_CODE_REGEX = /^\d{5}$/;

export const REQUIRED_FIELD_MESSAGE = "Ce champ est obligatoire.";
export const INVALID_EMAIL_MESSAGE = "Format d'email invalide.";
export const INVALID_POSTAL_CODE_MESSAGE =
  "Le code postal doit contenir 5 chiffres.";
export const INVALID_LAST_NAME_MESSAGE =
  "Le nom doit contenir au moins 2 lettres.";
export const INVALID_FIRST_NAME_MESSAGE =
  "Le prénom doit contenir au moins 2 lettres.";
export const EMAIL_OR_PHONE_REQUIRED_MESSAGE =
  "Renseignez un email ou au moins un numéro de téléphone (fixe ou mobile).";

export type PartyAddressShape = {
  addressLine: string;
  addressFloorApartment: string;
  addressBuildingResidence: string;
  postBox: string;
  postalCode: string;
  city: string;
};

export type PartyAddressFieldErrors = Partial<
  Record<"addressLine" | "postalCode" | "city", string>
>;

export function hasAnyAddressFieldFilled(value: PartyAddressShape): boolean {
  return Boolean(
    value.addressLine.trim() ||
    value.addressFloorApartment.trim() ||
    value.addressBuildingResidence.trim() ||
    value.postBox.trim() ||
    value.postalCode.trim() ||
    value.city.trim(),
  );
}

export function validatePartyAddressFields(
  value: PartyAddressShape,
): PartyAddressFieldErrors {
  const errors: PartyAddressFieldErrors = {};

  if (!hasAnyAddressFieldFilled(value)) {
    return errors;
  }

  if (!value.addressLine.trim()) {
    errors.addressLine = REQUIRED_FIELD_MESSAGE;
  }

  if (!value.postalCode.trim()) {
    errors.postalCode = REQUIRED_FIELD_MESSAGE;
  } else if (!isValidPostalCode(value.postalCode)) {
    errors.postalCode = INVALID_POSTAL_CODE_MESSAGE;
  }

  if (!value.city.trim()) {
    errors.city = REQUIRED_FIELD_MESSAGE;
  }

  return errors;
}

export function isPartyAddressComplete(value: PartyAddressShape): boolean {
  return Object.keys(validatePartyAddressFields(value)).length === 0;
}

export type PartyProfileValidationOptions = {
  /** @deprecated Prefer requireEmailOrPhone for individual profiles. */
  requireEmail?: boolean;
  requireEmailOrPhone?: boolean;
};

export type PartyProfileShape = PartyAddressShape & {
  email: string;
  fixedPhone: { value: string };
  mobilePhone: { value: string };
};

export type PartyProfileFieldErrors = Partial<
  Record<
    | "email"
    | "addressLine"
    | "postalCode"
    | "city"
    | "fixedPhone"
    | "mobilePhone",
    string
  >
>;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return z.string().email().safeParse(trimmed).success;
}

export function isValidPostalCode(value: string): boolean {
  return POSTAL_CODE_REGEX.test(value.trim());
}

export function isValidLastName(value: string): boolean {
  return SEARCH_NAME_REGEX.test(value.trim());
}

export function isValidOptionalFirstName(value: string): boolean {
  if (!value.trim()) return true;
  return SEARCH_NAME_REGEX.test(value.trim());
}

export function validateIndividualContactFields(value: {
  email: string;
  fixedPhone: { value: string };
  mobilePhone: { value: string };
}): Partial<Record<"email" | "fixedPhone" | "mobilePhone", string>> {
  const errors: Partial<
    Record<"email" | "fixedPhone" | "mobilePhone", string>
  > = {};

  const hasEmail = value.email.trim().length > 0;
  const hasFixed = value.fixedPhone.value.trim().length > 0;
  const hasMobile = value.mobilePhone.value.trim().length > 0;
  const emailValid = hasEmail && isValidEmail(value.email);
  const fixedValid =
    hasFixed && isOptionalFrenchPhoneValid(value.fixedPhone.value);
  const mobileValid =
    hasMobile && isOptionalFrenchPhoneValid(value.mobilePhone.value);

  if (hasEmail && !isValidEmail(value.email)) {
    errors.email = INVALID_EMAIL_MESSAGE;
  }

  if (hasFixed && !isOptionalFrenchPhoneValid(value.fixedPhone.value)) {
    errors.fixedPhone = FRENCH_PHONE_INVALID_MESSAGE;
  }

  if (hasMobile && !isOptionalFrenchPhoneValid(value.mobilePhone.value)) {
    errors.mobilePhone = FRENCH_PHONE_INVALID_MESSAGE;
  }

  const hasValidContact = emailValid || fixedValid || mobileValid;
  if (
    !hasValidContact &&
    !errors.email &&
    !errors.fixedPhone &&
    !errors.mobilePhone
  ) {
    errors.email = EMAIL_OR_PHONE_REQUIRED_MESSAGE;
  }

  return errors;
}

export function isIndividualContactComplete(value: {
  email: string;
  fixedPhone: { value: string };
  mobilePhone: { value: string };
}): boolean {
  return Object.keys(validateIndividualContactFields(value)).length === 0;
}

export function validatePartyProfileFields(
  value: PartyProfileShape,
  options?: PartyProfileValidationOptions,
): PartyProfileFieldErrors {
  const errors: PartyProfileFieldErrors = {};
  const requireEmailOrPhone = options?.requireEmailOrPhone ?? false;
  const requireEmail = options?.requireEmail ?? !requireEmailOrPhone;

  if (requireEmailOrPhone) {
    Object.assign(errors, validateIndividualContactFields(value));
  } else if (requireEmail) {
    if (!value.email.trim()) {
      errors.email = REQUIRED_FIELD_MESSAGE;
    } else if (!isValidEmail(value.email)) {
      errors.email = INVALID_EMAIL_MESSAGE;
    }
  }

  Object.assign(errors, validatePartyAddressFields(value));

  if (
    value.fixedPhone.value.trim() &&
    !isOptionalFrenchPhoneValid(value.fixedPhone.value) &&
    !errors.fixedPhone
  ) {
    errors.fixedPhone = FRENCH_PHONE_INVALID_MESSAGE;
  }

  if (
    value.mobilePhone.value.trim() &&
    !isOptionalFrenchPhoneValid(value.mobilePhone.value) &&
    !errors.mobilePhone
  ) {
    errors.mobilePhone = FRENCH_PHONE_INVALID_MESSAGE;
  }

  return errors;
}

export function isPartyProfileComplete(
  value: PartyProfileShape,
  options?: PartyProfileValidationOptions,
): boolean {
  return Object.keys(validatePartyProfileFields(value, options)).length === 0;
}

export type CompanyProfileShape = PartyProfileShape & {
  companyDenomination: string;
  apeCode: string;
};

export type CompanyProfileFieldErrors = PartyProfileFieldErrors &
  Partial<Record<"companyDenomination" | "apeCode", string>>;

export function validateCompanyProfileFields(
  value: CompanyProfileShape,
): CompanyProfileFieldErrors {
  const errors: CompanyProfileFieldErrors = validatePartyProfileFields(value, {
    requireEmail: false,
  });

  if (!value.companyDenomination.trim()) {
    errors.companyDenomination = REQUIRED_FIELD_MESSAGE;
  }

  if (!value.apeCode.trim()) {
    errors.apeCode = REQUIRED_FIELD_MESSAGE;
  }

  return errors;
}

export function isCompanyProfileComplete(value: CompanyProfileShape): boolean {
  return Object.keys(validateCompanyProfileFields(value)).length === 0;
}

export function isCompanyContactEditComplete(
  value: Pick<
    CompanyProfileShape,
    "email" | "fixedPhone" | "mobilePhone"
  > | null,
): boolean {
  if (!value) return false;
  return (
    Object.keys(
      validatePartyProfileFields(
        {
          email: value.email,
          addressLine: "",
          addressFloorApartment: "",
          addressBuildingResidence: "",
          postBox: "",
          postalCode: "",
          city: "",
          fixedPhone: value.fixedPhone,
          mobilePhone: value.mobilePhone,
        },
        { requireEmail: false },
      ),
    ).length === 0
  );
}

export type IndividualIdentityErrors = Partial<
  Record<"firstName" | "lastName", string>
>;

export function validateIndividualIdentity(value: {
  firstName: string;
  lastName: string;
}): IndividualIdentityErrors {
  const errors: IndividualIdentityErrors = {};

  if (!value.lastName.trim()) {
    errors.lastName = REQUIRED_FIELD_MESSAGE;
  } else if (!isValidLastName(value.lastName)) {
    errors.lastName = INVALID_LAST_NAME_MESSAGE;
  }

  if (value.firstName.trim() && !isValidOptionalFirstName(value.firstName)) {
    errors.firstName = INVALID_FIRST_NAME_MESSAGE;
  }

  return errors;
}

export function isIndividualIdentityComplete(value: {
  firstName: string;
  lastName: string;
}): boolean {
  return Object.keys(validateIndividualIdentity(value)).length === 0;
}
